/**
 * Signed-in OPERATOR credential (PIN sign-in → operator bearer token).
 *
 * The paired device token (deviceToken.ts) is the perimeter; the operator
 * token says WHO is working at it. `POST /floor/signin` mints it from a PIN;
 * every tool call then carries it as `Authorization: Bearer` next to the
 * device header. The API tolerates its absence on reads and refuses writes
 * without it (401 OPERATOR_REQUIRED), so the app gates the hub behind sign-in
 * and drops back to the PIN pad when the server says "sign in again".
 *
 * Held in sessionStorage: it dies with the tab, never outlives the token's own
 * 12 h expiry (checked client-side too), and is never written to localStorage
 * next to the device token.
 */

const OPERATOR_KEY = 'warehouselogix.floor.operator';

export interface OperatorSession {
  token: string;
  employeeId: string;
  displayName: string;
  /** ISO-8601 expiry, as the API minted it. */
  expiresAt: string;
}

type Listener = () => void;
const listeners = new Set<Listener>();

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

/** Subscribe to sign-in / sign-out. Returns an unsubscribe fn. */
export function subscribeOperator(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function read(): OperatorSession | null {
  try {
    const raw = window.sessionStorage.getItem(OPERATOR_KEY);
    if (raw === null) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<OperatorSession>;
    if (
      typeof parsed.token !== 'string' ||
      typeof parsed.employeeId !== 'string' ||
      typeof parsed.displayName !== 'string' ||
      typeof parsed.expiresAt !== 'string'
    ) {
      return null;
    }
    return {
      token: parsed.token,
      employeeId: parsed.employeeId,
      displayName: parsed.displayName,
      expiresAt: parsed.expiresAt,
    };
  } catch {
    return null;
  }
}

// useSyncExternalStore needs a stable snapshot: cache the parsed value and
// only replace it when the stored string changes.
let cachedRaw: string | null | undefined;
let cachedSession: OperatorSession | null = null;

/** The signed-in operator, or null when nobody is (or the token has expired). */
export function getOperator(): OperatorSession | null {
  let raw: string | null = null;
  try {
    raw = window.sessionStorage.getItem(OPERATOR_KEY);
  } catch {
    raw = null;
  }
  if (raw !== cachedRaw) {
    cachedRaw = raw;
    cachedSession = read();
  }
  if (cachedSession !== null && Date.parse(cachedSession.expiresAt) <= Date.now()) {
    return null;
  }
  return cachedSession;
}

/** Bearer token for the transport, or null when nobody is signed in. */
export function getOperatorToken(): string | null {
  return getOperator()?.token ?? null;
}

export function setOperator(session: OperatorSession): void {
  try {
    window.sessionStorage.setItem(OPERATOR_KEY, JSON.stringify(session));
  } catch {
    // Storage unavailable (private mode): the session lives only in this
    // module's cache for the life of the page.
    cachedRaw = undefined;
    cachedSession = session;
  }
  emit();
}

/** Sign out (switch user, or the server said "sign in again"). */
export function clearOperator(): void {
  try {
    window.sessionStorage.removeItem(OPERATOR_KEY);
  } catch {
    // ignore
  }
  cachedRaw = undefined;
  cachedSession = null;
  emit();
}
